<template>
  <section v-if="volunteer?.length" class="volunteer pe-section">
    <h3>
      Volunteer Experience
      <SectionActions
        v-show="isPowerEdit"
        class="actions"
        section="volunteer"
      />
    </h3>
    <template
      v-for="(exp, index) in cOrderedVolunteering"
      :key="exp.organization"
    >
      <section
        v-if="exp.position || exp.summary || exp.organization"
        class="volunteerExp"
      >
        <ItemActions
          v-show="isPowerEdit"
          class="itemActions"
          :index="index"
          section="volunteer"
          :uuid="exp.uuid"
          :actions="['edit', 'delete']"
        />
        <section class="title">
          <div class="details">
            <div class="position">
              <div
                class="name"
                v-editable="isPowerEdit"
                :data-target-path="`resume.volunteer.${index}.position`"
                v-text="exp.position"
              ></div>
              <div
                v-editable="isPowerEdit"
                :data-target-path="`resume.volunteer.${index}.organization`"
                class="org"
                v-text="exp.organization"
              ></div>
            </div>
            <div class="location"></div>
          </div>
          <div class="dates">
            <div>
              <DateDisplay
                :date="exp.startDate"
                :updatePath="`resume.volunteer.${index}.startDate`"
                :isEditable="isPowerEdit"
                :isInvalid="!checkDates(exp.startDate, exp.endDate)"
                @mouseenter="
                  $renderTooltip(
                    $event,
                    'Invalid date range',
                    !checkDates(exp.startDate, exp.endDate)
                  )
                "
              />
              <span v-if="exp.startDate"> - </span>
              <DateDisplay
                :date="exp.endDate"
                :updatePath="`resume.volunteer.${index}.endDate`"
                hasCurrentOption
                :isEditable="isPowerEdit"
                :isInvalid="!checkDates(exp.startDate, exp.endDate)"
                @mouseenter="
                  $renderTooltip(
                    $event,
                    'Invalid date range',
                    !checkDates(exp.startDate, exp.endDate)
                  )
                "
              />
            </div>
          </div>
        </section>
        <section class="description">
          <div
            v-if="exp.summary"
            class="summary"
            v-word-count
            v-editable="isPowerEdit"
            :data-target-path="`resume.volunteer.${index}.summary`"
            v-text="exp.summary"
            @click="
              evt =>
                openRephrasePopup(
                  `resume.volunteer.${index}.summary`,
                  evt.target
                )
            "
          ></div>
          <Highlights
            v-if="exp.highlights"
            :highlights="exp.highlights"
            :targetPath="`resume.volunteer.${index}.highlights`"
          />
        </section>
      </section>
    </template>
  </section>
</template>

<script lang="ts">
import { defineComponent, PropType, computed } from 'vue'
import { Resume } from '@/types/resume/v1'
import DateDisplay from './support/DateDisplay.vue'
import { useStructuredResumeStore } from '@/store/pinia/structuredResume'
import SectionActions from './SectionActions.vue'
import ItemActions from './ItemActions.vue'
import Highlights from './Highlights.vue'
import sortItems from '@/lib/dates/dateRangeSorter'
import { checkDates } from '@/lib/dates/dateHelper'
import { usePowerEditStore } from '@/store/pinia/powerEdit'

export default defineComponent({
  name: 'Volunteer',
  components: {
    DateDisplay,
    SectionActions,
    ItemActions,
    Highlights,
  },
  props: {
    volunteer: {
      type: Object as PropType<Resume.Volunteer[]>,
      required: false,
    },
  },
  setup(props) {
    const store = useStructuredResumeStore()
    const powerEditStore = usePowerEditStore()
    const { closePopup, openRephrasePopup } = powerEditStore

    const cOrderedVolunteering = computed(() => {
      return sortItems(props.volunteer || [])
    })

    return {
      isPowerEdit: store.isPowerEdit,
      cOrderedVolunteering,
      checkDates,
      closePopup,
      openRephrasePopup,
    }
  },
})
</script>

<style lang="postcss" scoped>
.volunteer {
  @apply leading-[inherit] text-base-em pt-6-em block;
  h3 {
    @apply leading-none text-xl-em flex items-center;
    .actions {
      @apply hidden;
    }
    &:hover {
      .actions {
        @apply flex;
      }
    }
  }
  .volunteerExp {
    @apply pt-4-em relative;
    .itemActions {
      @apply hidden;
    }
    &:hover {
      .itemActions {
        @apply flex;
      }
    }
    .title {
      @apply flex flex-row justify-between items-start text-base-em mb-2-em;
      .details {
        .name {
          @apply font-semibold;
        }
        .org {
          @apply italic text-sm-em;
        }
      }
    }
    .dates {
      @apply text-sm-em;
    }
    .description {
      @apply leading-[inherit] text-sm-em;
      .summary {
        @apply mb-2-em whitespace-pre-wrap;
      }
      .highlights {
        ul {
          @apply list-disc list-outside ml-4-em;
        }
      }
    }
  }
}
</style>
